import React, { useState, useEffect } from 'react';
import { useRoleContext } from '../context/RoleContext';
import { fetchBookings, updateBookingStatus } from '../services/bookingService';
import { type User } from '../services/graphql/authService';
import ProgressDialog from './ProgressDialog';
import { useServiceKey } from '../hooks/useServiceKey';
import { BookingsListLayout } from './BookingsListLayout/BookingsListLayout';
import { type BookingRecord } from '../services/graphql/bookingService';
import {
  getAllUsers,
  getAllServiceProviders,
  updateUserRole,
  updateUserStatus,
  assignUserToServiceProvider,
  createServiceProvider,
  updateServiceProvider,
  type UserProfile,
  type ServiceProvider,
} from '../services/graphql/userService';
import ServiceProviderEditor, { type ServiceProviderFormData } from './ServiceProviderEditor/ServiceProviderEditor';
import type { CreateServiceProviderInput, UpdateServiceProviderInput } from '../services/graphql/types';

// Extend AdminDashboard props to accept currentUser
interface AdminDashboardProps {
  currentUser: User | null;
}

type TabType = 'bookings' | 'users' | 'serviceProviders';

// Component for SuperAdmin to manage bookings, users, and service providers
const AdminDashboard: React.FC<AdminDashboardProps> = ({ currentUser }) => {
  const [isAdmin, setIsAdmin] = useState(false);
  const [activeTab, setActiveTab] = useState<TabType>('bookings');

  // Bookings state
  const [bookings, setBookings] = useState<BookingRecord[]>([]);

  // Users state
  const [users, setUsers] = useState<UserProfile[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState<'all' | 'customer' | 'serviceprovideradmin' | 'superadmin'>('all');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');

  // Service Providers state
  const [serviceProviders, setServiceProviders] = useState<ServiceProvider[]>([]);
  const [spSearchTerm, setSpSearchTerm] = useState('');
  const [expandedSpId, setExpandedSpId] = useState<string | null>(null);

  // Modal state for users
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<UserProfile | null>(null);
  const [newRole, setNewRole] = useState<'customer' | 'serviceprovideradmin' | 'superadmin'>('customer');
  const [selectedServiceProviderId, setSelectedServiceProviderId] = useState<string>('');

  // Modal state for service provider editor
  const [showSpEditor, setShowSpEditor] = useState(false);
  const [editingServiceProvider, setEditingServiceProvider] = useState<ServiceProvider | null>(null);

  // General state
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const { isValidKey } = useServiceKey();
  const { userProfile } = useRoleContext();

  useEffect(() => {
    if (!userProfile) {
      setIsAdmin(false);
      setLoading(false);
      return;
    }
    if (userProfile.role === 'superadmin') {
      setIsAdmin(true);
      setError(null);
      loadAllData();
    } else {
      setIsAdmin(false);
      setError('Unauthorized: You must be a superadmin to access this page.');
      setLoading(false);
    }
  }, [userProfile]);

  // Load all data
  const loadAllData = async () => {
    setLoading(true);
    setError(null);

    try {
      const [bookingsResult, usersResult, providersResult] = await Promise.all([
        fetchBookings(),
        getAllUsers(true),
        getAllServiceProviders(true),
      ]);

      // Process bookings
      if (bookingsResult.error) {
        console.error('Error in fetchBookings:', bookingsResult.error);
      } else if (bookingsResult.data) {
        const transformedBookings: BookingRecord[] = bookingsResult.data.map((booking: any) => ({
          ...booking,
          pickup_location: booking.pickup_location || '',
          dropoff_location: booking.dropoff_location || '',
          pickup_date: booking.pickup_date || '',
          pickup_time: booking.pickup_time || '',
          pickup_type: booking.pickup_type || 'scheduled',
          passenger_name: booking.passenger_name || '',
          company_name: booking.company_name || '',
          country_code: booking.country_code || '',
          passenger_mobile: booking.passenger_mobile || '',
          passenger_email: booking.passenger_email || '',
          payment_method: booking.payment_method || '',
          communication_prefs: booking.communication_prefs || { voice: false, sms: false, whatsapp: false, email: false },
          passengers: booking.passengers || 1,
          luggage: booking.luggage || 0,
          selected_ride: booking.selected_ride || '',
          estimated_duration: booking.estimated_duration || null,
          status: booking.status || 'pending',
          is_verified: booking.is_verified || false,
          user_id: booking.user_id || null,
          booking_number: booking.booking_number || '',
          status_reason: booking.status_reason || '',
          serviceprovider_id: booking.serviceprovider_id || '',
          service_provider_name: booking.service_provider?.serviceprovider_name || '',
          $createdAt: booking.created_at || '',
          $updatedAt: booking.updated_at || '',
        }));
        setBookings(transformedBookings);
      }

      // Process users
      if (usersResult.error) {
        console.error('Error fetching users:', usersResult.error);
      } else {
        setUsers(usersResult.data || []);
      }

      // Process service providers
      if (providersResult.data) {
        setServiceProviders(providersResult.data);
      }
    } catch (err: any) {
      console.error('Exception loading data:', err);
      setError(`Failed to load data: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Filter users
  const filteredUsers = users.filter((user) => {
    const matchesSearch =
      searchTerm === '' ||
      user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (user.full_name && user.full_name.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesRole = roleFilter === 'all' || user.role === roleFilter;
    const matchesStatus =
      statusFilter === 'all' ||
      (statusFilter === 'active' && user.is_active) ||
      (statusFilter === 'inactive' && !user.is_active);
    return matchesSearch && matchesRole && matchesStatus;
  });

  // Filter service providers
  const filteredServiceProviders = serviceProviders.filter((sp) => {
    return (
      spSearchTerm === '' ||
      sp.serviceprovider_name.toLowerCase().includes(spSearchTerm.toLowerCase()) ||
      sp.service_key.toLowerCase().includes(spSearchTerm.toLowerCase()) ||
      sp.contact_email.toLowerCase().includes(spSearchTerm.toLowerCase())
    );
  });

  // User action handlers
  const handleRoleChange = async () => {
    if (!selectedUser) return;
    setLoading(true);
    setShowRoleModal(false);
    const serviceProviderId = newRole === 'serviceprovideradmin' ? selectedServiceProviderId : undefined;
    const result = await updateUserRole(selectedUser.id, newRole, serviceProviderId);
    if (result.error) {
      setError(result.error.message);
    } else {
      setSuccessMessage(`User role updated to ${newRole}`);
      setTimeout(() => setSuccessMessage(null), 3000);
      loadAllData();
    }
    setSelectedUser(null);
    setLoading(false);
  };

  const handleStatusChange = async () => {
    if (!selectedUser) return;
    setLoading(true);
    setShowStatusModal(false);
    const newStatus = !selectedUser.is_active;
    const result = await updateUserStatus(selectedUser.id, newStatus);
    if (result.error) {
      setError(result.error.message);
    } else {
      setSuccessMessage(`User ${newStatus ? 'activated' : 'deactivated'} successfully`);
      setTimeout(() => setSuccessMessage(null), 3000);
      loadAllData();
    }
    setSelectedUser(null);
    setLoading(false);
  };

  const handleAssignServiceProvider = async () => {
    if (!selectedUser || !selectedServiceProviderId) return;
    setLoading(true);
    setShowAssignModal(false);
    const result = await assignUserToServiceProvider(selectedUser.id, selectedServiceProviderId);
    if (result.error) {
      setError(result.error.message);
    } else {
      setSuccessMessage('User assigned to service provider successfully');
      setTimeout(() => setSuccessMessage(null), 3000);
      loadAllData();
    }
    setSelectedUser(null);
    setSelectedServiceProviderId('');
    setLoading(false);
  };

  const openRoleModal = (user: UserProfile) => {
    setSelectedUser(user);
    setNewRole(user.role);
    setSelectedServiceProviderId(user.serviceprovider_id || '');
    setShowRoleModal(true);
  };

  const openStatusModal = (user: UserProfile) => {
    setSelectedUser(user);
    setShowStatusModal(true);
  };

  const openAssignModal = (user: UserProfile) => {
    setSelectedUser(user);
    setSelectedServiceProviderId(user.serviceprovider_id || '');
    setShowAssignModal(true);
  };

  const getServiceProviderName = (id: string | null | undefined) => {
    if (!id) return '-';
    const provider = serviceProviders.find((sp) => sp.id === id);
    return provider?.serviceprovider_name || id;
  };

  // Service Provider Editor handlers
  const openCreateSpEditor = () => {
    setEditingServiceProvider(null);
    setShowSpEditor(true);
  };

  const openEditSpEditor = (sp: ServiceProvider) => {
    setEditingServiceProvider(sp);
    setShowSpEditor(true);
  };

  const handleSaveServiceProvider = async (formData: ServiceProviderFormData) => {
    setLoading(true);
    setShowSpEditor(false);

    try {
      // Serialize attributes to JSON string
      const attributesJson = formData.attributes ? JSON.stringify(formData.attributes) : undefined;

      if (editingServiceProvider) {
        // Update existing service provider
        const updateInput: UpdateServiceProviderInput = {
          id: editingServiceProvider.id,
          serviceProviderName: formData.serviceprovider_name,
          contactEmail: formData.contact_email,
          contactPhone: formData.contact_phone || undefined,
          primaryColor: formData.primary_color,
          logoUrl: formData.logo_url || undefined,
          isActive: formData.is_active,
          maxDistance: formData.max_distance,
          defaultLocation: formData.default_location,
          defaultZoom: formData.default_zoom,
          timezone: formData.timezone,
          attributesJson,
        };
        const result = await updateServiceProvider(updateInput);
        if (result.error) {
          setError(result.error.message);
        } else {
          setSuccessMessage('Service provider updated successfully');
          setTimeout(() => setSuccessMessage(null), 3000);
          loadAllData();
        }
      } else {
        // Create new service provider
        const createInput: CreateServiceProviderInput = {
          serviceProviderName: formData.serviceprovider_name,
          serviceKey: formData.service_key,
          contactEmail: formData.contact_email,
          contactPhone: formData.contact_phone || undefined,
          primaryColor: formData.primary_color,
          logoUrl: formData.logo_url || undefined,
          maxDistance: formData.max_distance,
          defaultLocation: formData.default_location,
          defaultZoom: formData.default_zoom,
          timezone: formData.timezone,
          attributesJson,
        };
        const result = await createServiceProvider(createInput);
        if (result.error) {
          setError(result.error.message);
        } else {
          setSuccessMessage('Service provider created successfully');
          setTimeout(() => setSuccessMessage(null), 3000);
          loadAllData();
        }
      }
    } catch (err: any) {
      setError(`Failed to save service provider: ${err.message}`);
    } finally {
      setEditingServiceProvider(null);
      setLoading(false);
    }
  };

  // Badge components
  const RoleBadge = ({ role }: { role: string }) => {
    const colors = {
      superadmin: 'bg-accent text-accent-foreground',
      serviceprovideradmin: 'bg-info text-info-foreground',
      customer: 'bg-secondary text-secondary-foreground',
    };
    const labels = {
      superadmin: 'SuperAdmin',
      serviceprovideradmin: 'Service Admin',
      customer: 'Customer',
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${colors[role as keyof typeof colors] || colors.customer}`}>
        {labels[role as keyof typeof labels] || role}
      </span>
    );
  };

  const StatusBadge = ({ isActive }: { isActive: boolean }) => (
    <span className={`px-2 py-1 rounded-full text-xs font-medium ${isActive ? 'bg-success/20 text-success-foreground' : 'bg-destructive/20 text-destructive-foreground'}`}>
      {isActive ? 'Active' : 'Inactive'}
    </span>
  );

  if (!isValidKey) {
    return (
      <div className="p-8">
        <div className="bg-red-100 p-4 rounded-lg text-red-700">
          <h2 className="text-xl font-bold mb-2">Invalid Service Key</h2>
          <p>The service key is not valid. Please use a valid service URL.</p>
        </div>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="p-8">
        <h1 className="text-2xl font-bold text-red-600">Access Denied</h1>
        <p className="mt-4">You do not have permission to access this page.</p>
      </div>
    );
  }

  if (loading && bookings.length === 0 && users.length === 0) {
    return <ProgressDialog isOpen={loading} message="Loading admin dashboard..." subMessage="Please wait while we load data." />;
  }

  return (
    <div className="container mx-auto p-8 bg-background text-foreground">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold">Admin Dashboard</h1>
          <button
            onClick={loadAllData}
            disabled={loading}
            className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 disabled:opacity-50"
          >
            {loading ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>

        {/* Success/Error Messages */}
        {successMessage && (
          <div className="mb-4 p-4 bg-success/20 text-success-foreground rounded-md">{successMessage}</div>
        )}
        {error && (
          <div className="mb-4 p-4 bg-destructive/20 text-destructive-foreground rounded-md">
            {error}
            <button onClick={() => setError(null)} className="ml-4 underline">Dismiss</button>
          </div>
        )}

        {/* Tabs */}
        <div className="mb-6 border-b border-border">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('bookings')}
              className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'bookings'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-foreground-muted hover:text-foreground hover:border-gray-300'
              }`}
            >
              Bookings ({bookings.length})
            </button>
            <button
              onClick={() => setActiveTab('users')}
              className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'users'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-foreground-muted hover:text-foreground hover:border-gray-300'
              }`}
            >
              Users ({users.length})
            </button>
            <button
              onClick={() => setActiveTab('serviceProviders')}
              className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'serviceProviders'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-foreground-muted hover:text-foreground hover:border-gray-300'
              }`}
            >
              Service Providers ({serviceProviders.length})
            </button>
          </nav>
        </div>

        {/* Bookings Tab */}
        {activeTab === 'bookings' && (
          <BookingsListLayout
            bookings={bookings}
            loading={loading}
            error={error}
            successMessage={null}
            currentUser={currentUser}
            mode="admin"
            title=""
            emptyMessage="No bookings found."
            loadingMessage="Loading bookings..."
            onConfirm={async (bookingId) => {
              const result = await updateBookingStatus(bookingId, 'confirmed');
              if (result.success) {
                loadAllData();
              } else {
                setError(result.error || 'Failed to confirm booking');
              }
            }}
            onCancel={async (bookingId) => {
              const result = await updateBookingStatus(bookingId, 'cancelled');
              if (result.success) {
                loadAllData();
              } else {
                setError(result.error || 'Failed to cancel booking');
              }
            }}
            showRefreshButton={false}
          />
        )}

        {/* Users Tab */}
        {activeTab === 'users' && (
          <>
            {/* Filters */}
            <div className="mb-6 flex flex-wrap gap-4">
              <input
                type="text"
                placeholder="Search by name or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="px-4 py-2 border rounded-md bg-input text-foreground w-64"
              />
              <select
                value={roleFilter}
                onChange={(e) => setRoleFilter(e.target.value as typeof roleFilter)}
                className="px-4 py-2 border rounded-md bg-input text-foreground"
              >
                <option value="all">All Roles</option>
                <option value="customer">Customers</option>
                <option value="serviceprovideradmin">Service Admins</option>
                <option value="superadmin">SuperAdmins</option>
              </select>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as typeof statusFilter)}
                className="px-4 py-2 border rounded-md bg-input text-foreground"
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>

            {/* Stats */}
            <div className="mb-6 grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="p-4 bg-background-paper rounded-lg shadow">
                <div className="text-sm text-muted-foreground">Total Users</div>
                <div className="text-2xl font-bold">{users.length}</div>
              </div>
              <div className="p-4 bg-background-paper rounded-lg shadow">
                <div className="text-sm text-muted-foreground">Customers</div>
                <div className="text-2xl font-bold">{users.filter((u) => u.role === 'customer').length}</div>
              </div>
              <div className="p-4 bg-background-paper rounded-lg shadow">
                <div className="text-sm text-muted-foreground">Service Admins</div>
                <div className="text-2xl font-bold">{users.filter((u) => u.role === 'serviceprovideradmin').length}</div>
              </div>
              <div className="p-4 bg-background-paper rounded-lg shadow">
                <div className="text-sm text-muted-foreground">SuperAdmins</div>
                <div className="text-2xl font-bold">{users.filter((u) => u.role === 'superadmin').length}</div>
              </div>
            </div>

            {/* Users Table */}
            <div className="bg-background-paper rounded-lg shadow overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-border">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Name</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Email</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Role</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Service Provider</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-background divide-y divide-border">
                    {filteredUsers.map((user) => (
                      <tr key={user.id}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-foreground">{user.full_name || 'N/A'}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-foreground">{user.email}</td>
                        <td className="px-6 py-4 whitespace-nowrap"><RoleBadge role={user.role} /></td>
                        <td className="px-6 py-4 whitespace-nowrap"><StatusBadge isActive={user.is_active} /></td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">{getServiceProviderName(user.serviceprovider_id)}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          <div className="flex gap-2">
                            <button
                              onClick={() => openRoleModal(user)}
                              className="px-3 py-1 bg-info/20 text-info-foreground rounded hover:bg-info/30 text-xs"
                              disabled={user.id === userProfile?.id}
                              title={user.id === userProfile?.id ? "You cannot change your own role" : "Change role"}
                            >
                              Role
                            </button>
                            <button
                              onClick={() => openStatusModal(user)}
                              className="px-3 py-1 bg-warning/20 text-warning-foreground rounded hover:bg-warning/30 text-xs"
                              disabled={user.id === userProfile?.id}
                              title={user.id === userProfile?.id ? "You cannot change your own status" : "Toggle status"}
                            >
                              {user.is_active ? 'Deactivate' : 'Activate'}
                            </button>
                            {user.role === 'serviceprovideradmin' && (
                              <button
                                onClick={() => openAssignModal(user)}
                                className="px-3 py-1 bg-secondary text-secondary-foreground rounded hover:bg-secondary/80 text-xs"
                              >
                                Assign SP
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                    {filteredUsers.length === 0 && (
                      <tr>
                        <td colSpan={6} className="px-6 py-8 text-center text-sm text-muted-foreground">
                          No users found matching your filters.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}

        {/* Service Providers Tab */}
        {activeTab === 'serviceProviders' && (
          <>
            {/* Search and Create Button */}
            <div className="mb-6 flex flex-wrap gap-4 items-center justify-between">
              <input
                type="text"
                placeholder="Search by name, key, or email..."
                value={spSearchTerm}
                onChange={(e) => setSpSearchTerm(e.target.value)}
                className="px-4 py-2 border rounded-md bg-input text-foreground w-64"
              />
              <button
                onClick={openCreateSpEditor}
                className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 flex items-center gap-2"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
                </svg>
                Create Service Provider
              </button>
            </div>

            {/* Stats */}
            <div className="mb-6 grid grid-cols-2 md:grid-cols-3 gap-4">
              <div className="p-4 bg-background-paper rounded-lg shadow">
                <div className="text-sm text-muted-foreground">Total Providers</div>
                <div className="text-2xl font-bold">{serviceProviders.length}</div>
              </div>
              <div className="p-4 bg-background-paper rounded-lg shadow">
                <div className="text-sm text-muted-foreground">Active</div>
                <div className="text-2xl font-bold">{serviceProviders.filter((sp) => sp.is_active).length}</div>
              </div>
              <div className="p-4 bg-background-paper rounded-lg shadow">
                <div className="text-sm text-muted-foreground">Inactive</div>
                <div className="text-2xl font-bold">{serviceProviders.filter((sp) => !sp.is_active).length}</div>
              </div>
            </div>

            {/* Service Providers Table with Expandable Rows */}
            <div className="bg-background-paper rounded-lg shadow overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-border">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider w-8"></th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Name</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Service Key</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Contact Email</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Color</th>
                    </tr>
                  </thead>
                  <tbody className="bg-background divide-y divide-border">
                    {filteredServiceProviders.map((sp) => {
                      const isExpanded = expandedSpId === sp.id;
                      const location = typeof sp.default_location === 'object' && sp.default_location
                        ? sp.default_location
                        : null;
                      const attrs = typeof sp.attributes === 'object' && sp.attributes
                        ? sp.attributes as Record<string, unknown>
                        : {};

                      return (
                        <React.Fragment key={sp.id}>
                          <tr
                            onClick={() => setExpandedSpId(isExpanded ? null : sp.id)}
                            className="cursor-pointer hover:bg-muted/30 transition-colors"
                          >
                            <td className="px-6 py-4 text-sm">
                              <span className="text-muted-foreground">{isExpanded ? '▼' : '▶'}</span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-foreground font-medium">{sp.serviceprovider_name}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground font-mono">{sp.service_key}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-foreground">{sp.contact_email}</td>
                            <td className="px-6 py-4 whitespace-nowrap"><StatusBadge isActive={sp.is_active} /></td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center gap-2">
                                <div className="w-6 h-6 rounded border" style={{ backgroundColor: sp.primary_color }} />
                                <span className="text-sm text-muted-foreground font-mono">{sp.primary_color}</span>
                              </div>
                            </td>
                          </tr>
                          {isExpanded && (
                            <tr className="bg-muted/20">
                              <td colSpan={6} className="px-6 py-4">
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
                                  {/* Basic Info */}
                                  <div className="space-y-2">
                                    <h4 className="font-semibold text-foreground">Basic Info</h4>
                                    <div><span className="text-muted-foreground">Phone:</span> {sp.contact_phone || '-'}</div>
                                    <div><span className="text-muted-foreground">Timezone:</span> {sp.timezone || '-'}</div>
                                    <div><span className="text-muted-foreground">Max Distance:</span> {sp.max_distance ? `${sp.max_distance} km` : '-'}</div>
                                    <div><span className="text-muted-foreground">Default Zoom:</span> {sp.default_zoom || '-'}</div>
                                  </div>

                                  {/* Location */}
                                  <div className="space-y-2">
                                    <h4 className="font-semibold text-foreground">Location</h4>
                                    {location ? (
                                      <>
                                        <div><span className="text-muted-foreground">Latitude:</span> {location.lat}</div>
                                        <div><span className="text-muted-foreground">Longitude:</span> {location.lng}</div>
                                        <a
                                          href={`https://www.google.com/maps?q=${location.lat},${location.lng}`}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="text-primary hover:underline"
                                          onClick={(e) => e.stopPropagation()}
                                        >
                                          View on Google Maps →
                                        </a>
                                      </>
                                    ) : (
                                      <div className="text-muted-foreground">No location set</div>
                                    )}
                                  </div>

                                  {/* Attributes */}
                                  <div className="space-y-2">
                                    <h4 className="font-semibold text-foreground">Configuration</h4>
                                    {Object.keys(attrs).length > 0 ? (
                                      <>
                                        {attrs.region && <div><span className="text-muted-foreground">Region:</span> {String(attrs.region)}</div>}
                                        {attrs.currencySymbol && <div><span className="text-muted-foreground">Currency:</span> {String(attrs.currencySymbol)}</div>}
                                        {attrs.vehicleTypes && <div><span className="text-muted-foreground">Vehicle Types:</span> {String(attrs.vehicleTypes)}</div>}
                                        {attrs.paymentTypes && <div><span className="text-muted-foreground">Payment Types:</span> {String(attrs.paymentTypes)}</div>}
                                        {attrs.communicationMethods && (
                                          <div>
                                            <span className="text-muted-foreground">Communication:</span>{' '}
                                            {Array.isArray(attrs.communicationMethods)
                                              ? (attrs.communicationMethods as string[]).join(', ')
                                              : String(attrs.communicationMethods)}
                                          </div>
                                        )}
                                      </>
                                    ) : (
                                      <div className="text-muted-foreground">No attributes configured</div>
                                    )}
                                  </div>

                                  {/* Feature Flags */}
                                  {Object.keys(attrs).length > 0 && (
                                    <div className="space-y-2">
                                      <h4 className="font-semibold text-foreground">Features</h4>
                                      <div className="flex flex-wrap gap-2">
                                        {attrs.showContactInfo && (
                                          <span className="px-2 py-1 bg-success/20 text-success-foreground text-xs rounded">Show Contact Info</span>
                                        )}
                                        {attrs.showComfortLevels && (
                                          <span className="px-2 py-1 bg-success/20 text-success-foreground text-xs rounded">Comfort Levels</span>
                                        )}
                                        {attrs.enableGlobalSearch && (
                                          <span className="px-2 py-1 bg-success/20 text-success-foreground text-xs rounded">Global Search</span>
                                        )}
                                      </div>
                                    </div>
                                  )}

                                  {/* Timestamps */}
                                  <div className="space-y-2">
                                    <h4 className="font-semibold text-foreground">Timestamps</h4>
                                    <div><span className="text-muted-foreground">Created:</span> {sp.created_at ? new Date(sp.created_at).toLocaleDateString() : '-'}</div>
                                    <div><span className="text-muted-foreground">Updated:</span> {sp.updated_at ? new Date(sp.updated_at).toLocaleDateString() : '-'}</div>
                                  </div>

                                  {/* Actions */}
                                  <div className="space-y-2">
                                    <h4 className="font-semibold text-foreground">Actions</h4>
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        openEditSpEditor(sp);
                                      }}
                                      className="px-4 py-2 bg-info/20 text-info-foreground rounded hover:bg-info/30 flex items-center gap-2"
                                    >
                                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                                        <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                                      </svg>
                                      Edit Service Provider
                                    </button>
                                  </div>
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                    {filteredServiceProviders.length === 0 && (
                      <tr>
                        <td colSpan={6} className="px-6 py-8 text-center text-sm text-muted-foreground">
                          No service providers found.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Role Change Modal */}
      {showRoleModal && selectedUser && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-background-paper p-6 rounded-lg shadow-lg max-w-md w-full m-4">
            <h2 className="text-xl font-bold mb-4">Change User Role</h2>
            <p className="mb-4 text-muted-foreground">
              Changing role for: <strong>{selectedUser.email}</strong>
            </p>
            <div className="mb-4">
              <label className="block text-sm font-medium mb-2">New Role</label>
              <select
                value={newRole}
                onChange={(e) => setNewRole(e.target.value as typeof newRole)}
                className="w-full px-4 py-2 border rounded-md bg-input text-foreground"
              >
                <option value="customer">Customer</option>
                <option value="serviceprovideradmin">Service Provider Admin</option>
                <option value="superadmin">SuperAdmin</option>
              </select>
            </div>
            {newRole === 'serviceprovideradmin' && (
              <div className="mb-4">
                <label className="block text-sm font-medium mb-2">Service Provider</label>
                <select
                  value={selectedServiceProviderId}
                  onChange={(e) => setSelectedServiceProviderId(e.target.value)}
                  className="w-full px-4 py-2 border rounded-md bg-input text-foreground"
                >
                  <option value="">Select a service provider...</option>
                  {serviceProviders.map((sp) => (
                    <option key={sp.id} value={sp.id}>{sp.serviceprovider_name}</option>
                  ))}
                </select>
              </div>
            )}
            <div className="flex gap-2 justify-end">
              <button onClick={() => setShowRoleModal(false)} className="px-4 py-2 bg-secondary text-secondary-foreground rounded-md hover:bg-secondary/80">Cancel</button>
              <button
                onClick={handleRoleChange}
                disabled={newRole === 'serviceprovideradmin' && !selectedServiceProviderId}
                className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 disabled:opacity-50"
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Status Change Modal */}
      {showStatusModal && selectedUser && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-background-paper p-6 rounded-lg shadow-lg max-w-md w-full m-4">
            <h2 className="text-xl font-bold mb-4">{selectedUser.is_active ? 'Deactivate User' : 'Activate User'}</h2>
            <p className="mb-4 text-muted-foreground">
              Are you sure you want to {selectedUser.is_active ? 'deactivate' : 'activate'}{' '}
              <strong>{selectedUser.email}</strong>?
            </p>
            {selectedUser.is_active && (
              <p className="mb-4 text-sm text-warning-foreground bg-warning/20 p-3 rounded">
                Deactivated users will not be able to log in until reactivated.
              </p>
            )}
            <div className="flex gap-2 justify-end">
              <button onClick={() => setShowStatusModal(false)} className="px-4 py-2 bg-secondary text-secondary-foreground rounded-md hover:bg-secondary/80">Cancel</button>
              <button
                onClick={handleStatusChange}
                className={`px-4 py-2 rounded-md ${selectedUser.is_active ? 'bg-destructive text-destructive-foreground hover:bg-destructive/90' : 'bg-success text-success-foreground hover:bg-success/90'}`}
              >
                {selectedUser.is_active ? 'Deactivate' : 'Activate'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Assign Service Provider Modal */}
      {showAssignModal && selectedUser && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-background-paper p-6 rounded-lg shadow-lg max-w-md w-full m-4">
            <h2 className="text-xl font-bold mb-4">Assign Service Provider</h2>
            <p className="mb-4 text-muted-foreground">
              Assigning service provider for: <strong>{selectedUser.email}</strong>
            </p>
            <div className="mb-4">
              <label className="block text-sm font-medium mb-2">Service Provider</label>
              <select
                value={selectedServiceProviderId}
                onChange={(e) => setSelectedServiceProviderId(e.target.value)}
                className="w-full px-4 py-2 border rounded-md bg-input text-foreground"
              >
                <option value="">Select a service provider...</option>
                {serviceProviders.map((sp) => (
                  <option key={sp.id} value={sp.id}>{sp.serviceprovider_name}</option>
                ))}
              </select>
            </div>
            <div className="flex gap-2 justify-end">
              <button onClick={() => setShowAssignModal(false)} className="px-4 py-2 bg-secondary text-secondary-foreground rounded-md hover:bg-secondary/80">Cancel</button>
              <button
                onClick={handleAssignServiceProvider}
                disabled={!selectedServiceProviderId}
                className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 disabled:opacity-50"
              >
                Assign
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Service Provider Editor Modal */}
      <ServiceProviderEditor
        isOpen={showSpEditor}
        onClose={() => {
          setShowSpEditor(false);
          setEditingServiceProvider(null);
        }}
        onSave={handleSaveServiceProvider}
        serviceProvider={editingServiceProvider}
        mode={editingServiceProvider ? 'edit' : 'create'}
      />
    </div>
  );
};

export default AdminDashboard;
